import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { computeVPDSeries, computeStabilityScore } from "../utility/vpd.js";
import { apiFetch } from "../utility/apiFetch.js";

export function useGrowsQuery() {
  return useQuery({
    queryKey: ["grows"],
    queryFn: () => apiFetch("grows"),
    staleTime: 60_000,
  });
}

export function useGrowQuery(id) {
  return useQuery({
    queryKey: ["grow", id],
    queryFn: () => apiFetch(`grows/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useGrowPhasesQuery(id) {
  return useQuery({
    queryKey: ["grow-phases", id],
    queryFn: () => apiFetch(`grows/${id}/phases`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useGrowNotesQuery(id) {
  return useQuery({
    queryKey: ["grow-notes", id],
    queryFn: () => apiFetch(`grows/${id}/notes`),
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useGrowHarvestsQuery(id) {
  return useQuery({
    queryKey: ["grow-harvests", id],
    queryFn: () => apiFetch(`grows/${id}/harvests`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCreateGrow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch("grows", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["grows"] });
      if (variables.inventory_item_id != null) {
        qc.invalidateQueries({ queryKey: ["inventory"] });
        qc.invalidateQueries({ queryKey: ["inventory", variables.inventory_item_id] });
        qc.invalidateQueries({ queryKey: ["inventory-low-stock"] });
      }
    },
  });
}

export function useUpdateGrow(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch(`grows/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grows"] });
      qc.invalidateQueries({ queryKey: ["grow", id] });
    },
  });
}

export function useDeleteGrow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiFetch(`grows/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grows"] }),
  });
}

export function useGrowPhaseSummariesQuery(id) {
  return useQuery({
    queryKey: ["grow-phase-summaries", id],
    queryFn: () => apiFetch(`grows/${id}/phase-summaries`),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useCompleteGrow(id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (note) => apiFetch(`grows/${id}/complete`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grows"] });
      qc.invalidateQueries({ queryKey: ["grow", id] });
    },
  });
}

export function useUpdateGrowPhase(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ phaseId, ...data }) => apiFetch(`grows/${growId}/phases/${phaseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grow-phases", growId] });
      qc.invalidateQueries({ queryKey: ["grows"] });
      qc.invalidateQueries({ queryKey: ["grow", growId] });
    },
  });
}

export function useAdvancePhase(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (phaseName) => apiFetch(`grows/${growId}/phases`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phase_name: phaseName }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grows"] });
      qc.invalidateQueries({ queryKey: ["grow", growId] });
      qc.invalidateQueries({ queryKey: ["grow-phases", growId] });
    },
  });
}

export function useCreateNote(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch(`grows/${growId}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grow-notes", growId] }),
  });
}

export function useDeleteNote(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId) => apiFetch(`grows/${growId}/notes/${noteId}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["grow-notes", growId] }),
  });
}

export function useCreateHarvest(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiFetch(`grows/${growId}/harvests`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grow-harvests", growId] });
      qc.invalidateQueries({ queryKey: ["grows"] });
    },
  });
}

export function useDeleteHarvest(growId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (harvestId) => apiFetch(`grows/${growId}/harvests/${harvestId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["grow-harvests", growId] });
      qc.invalidateQueries({ queryKey: ["grows"] });
    },
  });
}

// Fetches temp+humidity for up to the last 24h (or since phase start, whichever
// is more recent), computes a VPD series, and returns a 0–100 stability score.
// Returns null when the current phase has no VPD target (Inoculation/Colonization).
export function useStabilityScore({ hostname, currentPhase }) {
  // Bucket to 5-minute intervals so the query key is stable between renders.
  const end = useMemo(() => Math.floor(Date.now() / 300_000) * 300_000, []);
  const phaseStart = currentPhase?.started_at ? new Date(currentPhase.started_at).getTime() : null;
  const start = phaseStart ? Math.max(phaseStart, end - 24 * 3600_000) : end - 24 * 3600_000;

  const enabled = !!hostname && !!currentPhase;

  const tempQuery = useQuery({
    queryKey: ["metric", "temperature", hostname, start, end],
    queryFn: async () => {
      const params = new URLSearchParams({
        metric: "temperature",
        hostname,
        start: Math.floor(start / 1000).toString(),
        end: Math.floor(end / 1000).toString(),
      });
      const res = await fetch(`${API}metrics/range?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`metrics/range ${res.status}`);
      return res.json();
    },
    enabled,
    staleTime: 300_000,
  });

  const humQuery = useQuery({
    queryKey: ["metric", "humidity", hostname, start, end],
    queryFn: async () => {
      const params = new URLSearchParams({
        metric: "humidity",
        hostname,
        start: Math.floor(start / 1000).toString(),
        end: Math.floor(end / 1000).toString(),
      });
      const res = await fetch(`${API}metrics/range?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`metrics/range ${res.status}`);
      return res.json();
    },
    enabled,
    staleTime: 300_000,
  });

  return useMemo(() => {
    if (!currentPhase) return null;
    const tempPoints = tempQuery.data?.[0]?.points ?? [];
    const humPoints  = humQuery.data?.[0]?.points  ?? [];
    const vpdSeries  = computeVPDSeries(tempPoints, humPoints);
    return computeStabilityScore(vpdSeries, currentPhase.phase_name);
  }, [tempQuery.data, humQuery.data, currentPhase]);
}
